import { Badge, Button, Card, RadioGroup } from "@design";
import { CheckCircle2, Telescope } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { useProposalContext } from "./ProposalContext";
import { ProposalDetails } from "./ProposalDetails";
import { DEPTHS, type ResearchProposal } from "./researchModel";
import { useProposalDecision } from "./useProposalDecision";

/**
 * A research run the assistant proposes (MHS-798): what the report will answer, the topics, the assumptions, where it
 * looks and how deep. Nothing runs until the chat's owner starts it; "Just answer now" declines it. Decided and
 * replaced proposals, and everyone else's view of a chat, are read-only.
 */
export function ResearchProposalCard({ proposal }: Readonly<{ proposal: ResearchProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.proposal" });
  const { readOnly } = useProposalContext();
  return (
    <Card>
      <section aria-label={t("label")} className="space-y-3">
        <div>
          <Badge variant="neutral" icon={<Telescope size={12} />}>
            {t("label")}
          </Badge>
        </div>
        {proposal.brief && <Text className="break-words">{proposal.brief}</Text>}
        <ProposalDetails proposal={proposal} />
        {proposal.status === "pending" && !readOnly ? (
          <ProposalChoice proposal={proposal} />
        ) : (
          <ProposalOutcome proposal={proposal} />
        )}
      </section>
    </Card>
  );
}

/** The owner's choice: the depth, then start or decline. Waits while a reply is on its way. */
function ProposalChoice({ proposal }: Readonly<{ proposal: ResearchProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.proposal" });
  const { busy } = useProposalContext();
  const { depth, setDepth, deciding, decide } = useProposalDecision(proposal);
  const disabled = busy || deciding !== null;
  return (
    <>
      <div className="space-y-1">
        <Text variant="caption" tone="tertiary">
          {t("depth")}
        </Text>
        <RadioGroup
          aria-label={t("depth")}
          value={depth}
          disabled={disabled}
          onChange={(value) => setDepth(value === "deep" ? "deep" : "standard")}
          options={DEPTHS.map((option) => ({
            value: option,
            label: t(`depthOption.${option}`),
            description: t(`depthTime.${option}`),
          }))}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Telescope size={14} />}
          loading={deciding === "run"}
          disabled={disabled}
          onClick={() => decide("run")}
        >
          {t("start")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={deciding === "decline"}
          disabled={disabled}
          onClick={() => decide("decline")}
        >
          {t("decline")}
        </Button>
      </div>
    </>
  );
}

/** A proposal that can't be acted on here: decided, replaced, or someone else's chat. */
function ProposalOutcome({ proposal }: Readonly<{ proposal: ResearchProposal }>) {
  const { t } = useTranslation("chat", { keyPrefix: "research.proposal" });
  return (
    <>
      <Text variant="body-sm" tone="secondary">
        {t(`depthChosen.${proposal.depth}`)}
      </Text>
      {proposal.status === "confirmed" ? (
        <div>
          <Badge variant="success" icon={<CheckCircle2 size={12} />}>
            {t("confirmed")}
          </Badge>
        </div>
      ) : (
        <Text variant="caption" tone="tertiary">
          {proposal.status === "pending" ? t("readOnly") : t(proposal.status)}
        </Text>
      )}
    </>
  );
}
